import type {
  FormButtonWidth,
  FormHeaderAlign,
  FormSubmitAlign,
  FormSubmitValues,
  FormWidgetCountry,
  MockResult,
  UserScenario,
} from '@/features/forms/components/shared/configurable';
import type { AdipaFormWidgetAttribute, AdipaFormWidgetProps } from './types';

export const DEFAULT_FORM_WIDGET_PROPS: AdipaFormWidgetProps = {
  variant: 'seminar-registration',
  platformCountry: 'CL',
  mockResult: 'success',
};

function parseJsonObjectAttribute(raw: string | null): FormSubmitValues | undefined {
  if (!raw) return undefined;

  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as FormSubmitValues;
    }
  } catch {
    // Atributo JSON malformado: lo ignoramos para no romper el render.
  }

  return undefined;
}

export const OBSERVED_FORM_WIDGET_ATTRIBUTES: AdipaFormWidgetAttribute[] = [
  'variant',
  'platform-country',
  'country',
  'submit-url',
  'profile-url',
  'terms-url',
  'register-url',
  'forgot-password-url',
  'social-providers',
  'social-divider-label',
  'google-auth-url',
  'google-auth-label',
  'countdown-deadline',
  'countdown-text',
  'source',
  'tracking-id',
  'page-url',
  'title-align',
  'form-title',
  'button-width',
  'submit-align',
  'prefill',
  'extra-fields',
  'recaptcha-site-key',
  'success-redirect-url',
  'naked',
  'data-preview-scenario',
  'data-preview-result',
  'mock-result',
];

export function readFormWidgetAttribute<T extends string>(
  element: HTMLElement,
  name: string,
  fallback: T,
): T {
  return (element.getAttribute(name) as T | null) ?? fallback;
}

export function readFormWidgetProps(element: HTMLElement): AdipaFormWidgetProps {
  const legacyCountry = element.getAttribute('country') as FormWidgetCountry | null;

  return {
    variant: readFormWidgetAttribute(element, 'variant', DEFAULT_FORM_WIDGET_PROPS.variant),
    platformCountry:
      (element.getAttribute('platform-country') as FormWidgetCountry | null) ??
      legacyCountry ??
      DEFAULT_FORM_WIDGET_PROPS.platformCountry,
    submitUrl: element.getAttribute('submit-url') ?? undefined,
    profileUrl: element.getAttribute('profile-url') ?? undefined,
    termsUrl: element.getAttribute('terms-url') ?? undefined,
    registerUrl: element.getAttribute('register-url') ?? undefined,
    forgotPasswordUrl: element.getAttribute('forgot-password-url') ?? undefined,
    socialProviders: element.getAttribute('social-providers') ?? undefined,
    socialDividerLabel: element.getAttribute('social-divider-label') ?? undefined,
    googleAuthUrl: element.getAttribute('google-auth-url') ?? undefined,
    googleAuthLabel: element.getAttribute('google-auth-label') ?? undefined,
    countdownDeadline: element.getAttribute('countdown-deadline') ?? undefined,
    countdownText: element.getAttribute('countdown-text') ?? undefined,
    source: element.getAttribute('source') ?? undefined,
    trackingId: element.getAttribute('tracking-id') ?? undefined,
    pageUrl: element.getAttribute('page-url') ?? window.location.href,
    prefill: parseJsonObjectAttribute(element.getAttribute('prefill')),
    extraFields: parseJsonObjectAttribute(element.getAttribute('extra-fields')),
    previewScenario:
      (element.getAttribute('data-preview-scenario') as UserScenario | null) ?? undefined,
    titleAlign: (element.getAttribute('title-align') as FormHeaderAlign | null) ?? undefined,
    formTitle: element.getAttribute('form-title') ?? undefined,
    submitAlign: (element.getAttribute('submit-align') as FormSubmitAlign | null) ?? undefined,
    buttonWidth: (element.getAttribute('button-width') as FormButtonWidth | null) ?? undefined,
    recaptchaSiteKey: element.getAttribute('recaptcha-site-key') ?? undefined,
    successRedirectUrl: element.getAttribute('success-redirect-url') ?? undefined,
    naked: element.hasAttribute('naked'),
    mockResult:
      (element.getAttribute('data-preview-result') as MockResult | null) ??
      readFormWidgetAttribute(element, 'mock-result', DEFAULT_FORM_WIDGET_PROPS.mockResult),
  };
}
